import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, Calendar, DollarSign, MapPin, Phone, Mail, 
  Trash2, Edit2, Plus, Search, Save, X, Sparkles, 
  Loader2, Cloud, Wifi, Lock, LogIn, LayoutDashboard,
  TrendingUp, Wallet, Building2, Home, Upload, Filter, ArrowRight
} from 'lucide-react';

// --- IMPORTAÇÕES DE GRÁFICOS ---
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// --- IMPORTAÇÕES DO FIREBASE ---
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query
} from 'firebase/firestore';

// --- SUAS CONFIGURAÇÕES ---
const firebaseConfig = {
  apiKey: "AIzaSyANct1gQW_Kw_SYUUtHQzTUY4T90N93Cu8",
  authDomain: "greatopportunity-80bfd.firebaseapp.com",
  projectId: "greatopportunity-80bfd",
  storageBucket: "greatopportunity-80bfd.firebasestorage.app",
  messagingSenderId: "716941916486",
  appId: "1:716941916486:web:4a50eb0ad58af102bfcd47"
};

// --- CORREÇÃO CRÍTICA PARA VERCEL ---
let db: any;

try {
  const app = initializeApp(firebaseConfig);
  db = getFirestore(app);
} catch (error) {
  console.warn("Erro ao iniciar Firebase.", error);
}

export default function App() {
  const APP_PASSWORD = "1234";

  // --- 🖼️ SUA NOVA LOGO ---
  const COMPANY_LOGO_URL = "/logo.png";

  // --- Estado ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inputPassword, setInputPassword] = useState('');
  const [activeTab, setActiveTab] = useState('clients'); 
  
  const [viewFilter, setViewFilter] = useState('All');

  const [clients, setClients] = useState<any[]>([]); 
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const [rawMetrics, setRawMetrics] = useState({
    totalRevenue: 0,
    resRevenue: 0,
    comRevenue: 0,
    resCount: 0,
    comCount: 0
  });

  const initialFormState = {
    firstName: '',
    lastName: '',
    startDate: '',
    regularPrice: '',
    frequency: 'Weekly',
    clientType: 'Residential',
    address: '',
    phone: '',
    email: '',
    deepCleanPrice: '',
    gateCode: '',      
    wifiPass: '',      
    pets: '',          
    entryMethod: ''    
  };
  const [formData, setFormData] = useState(initialFormState);

  // --- 1. LER DADOS ---
  useEffect(() => {
    if (!db || !isAuthenticated) return; 

    setLoading(true);
    try {
      const q = query(collection(db, "clientes")); 
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const clientsData: any[] = [];
        let totalRev = 0;
        let resRev = 0;
        let comRev = 0;
        let rCount = 0;
        let cCount = 0;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const safeClientType = (data.clientType || 'Residential').trim();
          
          const price = parseFloat(data.regularPrice) || 0;
          let monthlyValue = 0;
          
          if (data.frequency === 'Daily') monthlyValue = price * 20; 
          else if (data.frequency === 'Weekly') monthlyValue = price * 4;
          else if (data.frequency === 'Bi-weekly') monthlyValue = price * 2;
          else if (data.frequency === 'Monthly') monthlyValue = price;
          else if (data.frequency === 'One-time') monthlyValue = price;

          clientsData.push({ 
            ...data, 
            clientType: safeClientType, 
            id: doc.id,
            monthlyValue: monthlyValue 
          });

          totalRev += monthlyValue;
          if (safeClientType === 'Commercial') {
            comRev += monthlyValue;
            cCount++;
          } else {
            resRev += monthlyValue;
            rCount++;
          }
        });

        clientsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        
        setClients(clientsData);
        setRawMetrics({
          totalRevenue: totalRev,
          resRevenue: resRev,
          comRevenue: comRev,
          resCount: rCount,
          comCount: cCount
        });
        setLoading(false);
      }, (error) => {
        console.error("Erro:", error);
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (err) {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // --- 2. CÁLCULOS DINÂMICOS ---
  const dashboardData = useMemo(() => {
    const filteredForDashboard = clients.filter(c => 
      viewFilter === 'All' || c.clientType === viewFilter
    );

    const ranking = [...filteredForDashboard]
      .sort((a, b) => b.monthlyValue - a.monthlyValue)
      .map(c => ({
        name: c.firstName,
        value: c.monthlyValue
      }));

    return { ranking };
  }, [clients, viewFilter]);


  // --- MANIPULADORES ---
  const handleFileUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("Isso importará os clientes. Continuar?")) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvText = event.target?.result as string;
        const lines = csvText.split('\n');
        const uniqueClients = new Map();
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
          if (cols.length < 3) continue;
          const nameRaw = cols[1]?.replace(/"/g, '').trim();
          const valueRaw = cols[2]?.replace(/"/g, '').replace('$', '').trim();
          const addressRaw = cols[3]?.replace(/"/g, '').trim();
          if (!nameRaw) continue;
          const cleanName = nameRaw.split('$')[0].trim(); 
          if (!uniqueClients.has(cleanName)) {
             uniqueClients.set(cleanName, {
                firstName: cleanName, lastName: '', startDate: new Date().toISOString().split('T')[0],
                regularPrice: valueRaw || '0', frequency: 'Weekly', clientType: 'Residential',
                address: addressRaw || '', phone: '', email: '', deepCleanPrice: '',
                gateCode: '', wifiPass: '', pets: '', entryMethod: '', createdAt: Date.now()
             });
          }
        }
        let count = 0;
        const clientsToImport = Array.from(uniqueClients.values());
        for (const clientData of clientsToImport) {
           const exists = clients.some((c: any) => c.firstName.toLowerCase() === (clientData as any).firstName.toLowerCase());
           if (!exists) { await addDoc(collection(db, "clientes"), clientData); count++; }
        }
        alert(`Importação concluída! ${count} adicionados.`);
      } catch (error) { alert("Erro na importação."); }
      setIsImporting(false);
    };
    reader.readAsText(file);
  };

  const handleLogin = (e: any) => {
    e.preventDefault();
    if (inputPassword === APP_PASSWORD) setIsAuthenticated(true);
    else alert("Senha incorreta!");
  };
  const handleInputChange = (e: any) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editingId) { await updateDoc(doc(db, "clientes", editingId), { ...formData }); alert("Atualizado!"); }
      else { await addDoc(collection(db, "clientes"), { ...formData, createdAt: Date.now() }); alert("Criado!"); }
      resetForm();
    } catch (error: any) { alert("Erro: " + error.message); }
    setIsSaving(false);
  };
  const handleDelete = async (id: string) => { if (window.confirm('Delete?')) try { await deleteDoc(doc(db, "clientes", id)); } catch (error) { console.error(error); } };
  const handleEdit = (client: any) => {
    setFormData({ ...client, clientType: client.clientType || 'Residential' });
    setEditingId(client.id);
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const resetForm = () => { setFormData(initialFormState); setEditingId(null); setIsFormOpen(false); };

  const filteredClientsList = clients.filter(client => {
    const matchesSearch = client.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          client.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = viewFilter === 'All' || client.clientType === viewFilter;
    return matchesSearch && matchesType;
  });

  const handleDashboardFilter = (type: string) => {
    setViewFilter(type);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
          {/* LOGO NA TELA DE LOGIN */}
          <div className="flex justify-center mb-6">
             <img 
               src={COMPANY_LOGO_URL} 
               alt="Great Opportunity Logo" 
               className="w-32 h-32 object-contain" 
             />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Great Opportunity App</h2>
          <p className="text-slate-500 mb-6">Secure ERP Login</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" placeholder="Passcode" value={inputPassword} onChange={(e) => setInputPassword(e.target.value)} className="w-full p-4 border rounded-xl text-center text-xl tracking-widest focus:ring-2 focus:ring-indigo-500 outline-none" />
            <button type="submit" className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 flex items-center justify-center gap-2"><LogIn size={20} /> Login</button>
          </form>
        </div>
      </div>
    );
  }

  const DashboardView = () => {
    const pieData = [
      { name: 'Residential', value: rawMetrics.resRevenue },
      { name: 'Commercial', value: rawMetrics.comRevenue },
    ];
    
    const chartHeight = Math.max(300, dashboardData.ranking.length * 50);

    return (
      <div className="space-y-6 animate-fade-in-down pb-20">
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            onClick={() => handleDashboardFilter('All')}
            className={`rounded-2xl p-5 shadow-lg cursor-pointer hover:scale-[1.02] transition-transform relative overflow-hidden ${viewFilter === 'All' ? 'bg-indigo-600 text-white ring-4 ring-indigo-200' : 'bg-white text-slate-800'}`}
          >
            <div className="flex items-center gap-2 opacity-80 mb-1 text-sm"><Wallet size={18}/> Total Revenue</div>
            <div className="text-3xl font-bold">${rawMetrics.totalRevenue.toLocaleString()}</div>
            {viewFilter === 'All' && <div className="absolute top-2 right-2 bg-white/20 p-1 rounded-full"><Sparkles size={16}/></div>}
          </div>
          
          <div 
            onClick={() => handleDashboardFilter('Residential')}
            className={`rounded-2xl p-5 shadow-sm border cursor-pointer hover:scale-[1.02] transition-transform relative ${viewFilter === 'Residential' ? 'bg-blue-600 text-white border-blue-600 ring-4 ring-blue-200' : 'bg-white text-slate-800 border-slate-100'}`}
          >
            <div className={`flex items-center gap-2 mb-1 text-sm font-bold ${viewFilter === 'Residential' ? 'text-white' : 'text-blue-600'}`}><Home size={18}/> Residential</div>
            <div className="text-2xl font-bold">${rawMetrics.resRevenue.toLocaleString()}</div>
            <div className={`text-xs ${viewFilter === 'Residential' ? 'text-blue-100' : 'text-slate-400'}`}>{rawMetrics.resCount} Clients</div>
          </div>

          <div 
            onClick={() => handleDashboardFilter('Commercial')}
            className={`rounded-2xl p-5 shadow-sm border cursor-pointer hover:scale-[1.02] transition-transform relative ${viewFilter === 'Commercial' ? 'bg-emerald-600 text-white border-emerald-600 ring-4 ring-emerald-200' : 'bg-white text-slate-800 border-slate-100'}`}
          >
            <div className={`flex items-center gap-2 mb-1 text-sm font-bold ${viewFilter === 'Commercial' ? 'text-white' : 'text-emerald-600'}`}><Building2 size={18}/> Commercial</div>
            <div className="text-2xl font-bold">${rawMetrics.comRevenue.toLocaleString()}</div>
            <div className={`text-xs ${viewFilter === 'Commercial' ? 'text-emerald-100' : 'text-slate-400'}`}>{rawMetrics.comCount} Clients</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="font-bold text-slate-700 mb-4">Revenue Split</h3>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value">
                    {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={index === 0 ? '#3B82F6' : '#10B981'} />))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
            <div className="flex justify-between items-center mb-4">
               <h3 className="font-bold text-slate-700">
                 Client Ranking ({viewFilter})
               </h3>
               {viewFilter !== 'All' && <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">Filtered</span>}
            </div>
            
            <div className="flex-1 min-h-[300px] max-h-[400px] overflow-y-auto pr-2">
              {dashboardData.ranking.length > 0 ? (
                <div style={{ height: `${chartHeight}px`, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={dashboardData.ranking} 
                      layout="vertical" 
                      margin={{top: 5, right: 30, left: 20, bottom: 5}}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100} 
                        tick={{fontSize: 11}} 
                        interval={0} 
                      />
                      <Tooltip formatter={(value) => `$${value}/mo`} />
                      <Bar dataKey="value" fill={viewFilter === 'Commercial' ? '#10B981' : viewFilter === 'Residential' ? '#3B82F6' : '#6366f1'} radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">No data found.</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center">
           <button 
             onClick={() => setActiveTab('clients')}
             className="flex items-center gap-2 text-indigo-600 bg-indigo-50 px-6 py-3 rounded-full font-bold hover:bg-indigo-100 transition-colors"
           >
             See Detailed {viewFilter} List <ArrowRight size={18} />
           </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
      <header className="bg-indigo-600 text-white shadow-lg sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
               src={COMPANY_LOGO_URL} 
               alt="Great Opportunity Logo" 
               className="w-10 h-10 object-contain bg-white rounded-full p-1"
            />
            <h1 className="text-lg md:text-xl font-bold">Great Opportunity App</h1>
          </div>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer flex items-center gap-1 text-xs bg-indigo-500 hover:bg-indigo-400 px-3 py-1.5 rounded-full transition-colors border border-indigo-400">
               <Upload size={14} /><span className="hidden sm:inline">Import</span>
               <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} disabled={isImporting} />
            </label>
            <div className="text-xs bg-indigo-700 px-2 py-1 rounded-full"><Wifi size={10} className={db ? "text-green-400" : "text-red-400"} /></div>
            <button onClick={() => setIsAuthenticated(false)}><Lock size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-28">
        {isImporting && (
           <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-xl flex flex-col items-center"><Loader2 className="animate-spin text-indigo-600 mb-2" size={32} /><p className="font-bold">Importing...</p></div>
           </div>
        )}

        {activeTab === 'clients' ? (
          <>
            {!isFormOpen && (
              <div className="mb-6">
                <div className="flex p-1 bg-slate-200 rounded-xl mb-4">
                  {['All', 'Residential', 'Commercial'].map((type) => (
                    <button key={type} onClick={() => setViewFilter(type)} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${viewFilter === type ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{type}</button>
                  ))}
                </div>
                <button onClick={() => { resetForm(); setIsFormOpen(true); }} className="w-full bg-indigo-600 text-white py-4 rounded-xl shadow-md font-bold text-lg flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all"><Plus size={24} /> Add New Client</button>
              </div>
            )}

            {loading && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-indigo-600" size={40} /></div>}

            {!loading && isFormOpen && (
              <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-5 mb-8 animate-fade-in-down">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <h2 className="text-xl font-bold text-slate-700">{editingId ? 'Edit Client' : 'New Client'}</h2>
                  <button onClick={() => setIsFormOpen(false)} className="p-2 bg-slate-100 rounded-full"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                     <label className="block text-xs font-bold text-indigo-600 uppercase mb-2">Client Type</label>
                     <div className="grid grid-cols-2 gap-4">
                        <label className={`flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer border transition-all ${formData.clientType === 'Residential' ? 'bg-white border-blue-500 shadow-sm text-blue-600' : 'border-transparent hover:bg-white/50 text-slate-500'}`}>
                           <input type="radio" name="clientType" value="Residential" checked={formData.clientType === 'Residential'} onChange={handleInputChange} className="hidden" />
                           <Home size={18} /> Residential
                        </label>
                        <label className={`flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer border transition-all ${formData.clientType === 'Commercial' ? 'bg-white border-emerald-500 shadow-sm text-emerald-600' : 'border-transparent hover:bg-white/50 text-slate-500'}`}>
                           <input type="radio" name="clientType" value="Commercial" checked={formData.clientType === 'Commercial'} onChange={handleInputChange} className="hidden" />
                           <Building2 size={18} /> Commercial
                        </label>
                     </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                    <h3 className="font-bold text-slate-500 mb-3 text-sm uppercase">Basic Info</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input required name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                      <input required name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                    </div>
                    <input required name="address" placeholder="Address" value={formData.address} onChange={handleInputChange} className="w-full p-3 border rounded-lg mb-3" />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                        <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                    <h3 className="font-bold text-slate-500 mb-3 text-sm uppercase">Agreement</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <select name="frequency" value={formData.frequency} onChange={handleInputChange} className="w-full p-3 border rounded-lg bg-white">
                        {/* ADICIONADO DAILY AQUI */}
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Bi-weekly">Bi-weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="One-time">One-time</option>
                      </select>
                      <input required type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} className="w-full p-3 border rounded-lg bg-white" />
                    </div>
                    <div className="relative mb-3">
                        <span className="absolute left-3 top-3 text-green-600 font-bold">$</span>
                        <input required type="number" name="regularPrice" placeholder="Regular Price" value={formData.regularPrice} onChange={handleInputChange} className="w-full pl-8 p-3 border border-green-200 bg-green-50 rounded-lg font-semibold text-green-700 placeholder-green-300" />
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                    <h3 className="font-bold text-slate-500 mb-3 text-sm uppercase">Access & Notes</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <input name="gateCode" placeholder="Gate Code" value={formData.gateCode} onChange={handleInputChange} className="w-full p-3 border rounded-lg text-sm" />
                        <input name="wifiPass" placeholder="Wifi" value={formData.wifiPass} onChange={handleInputChange} className="w-full p-3 border rounded-lg text-sm" />
                        <input name="pets" placeholder="Pets" value={formData.pets} onChange={handleInputChange} className="w-full p-3 border rounded-lg text-sm" />
                        <input name="entryMethod" placeholder="Entry Method" value={formData.entryMethod} onChange={handleInputChange} className="w-full p-3 border rounded-lg text-sm" />
                    </div>
                  </div>
                  <button type="submit" disabled={isSaving} className="w-full bg-green-600 text-white py-4 rounded-xl font-bold shadow-lg hover:bg-green-700 flex items-center justify-center gap-2 text-lg">
                    {isSaving ? <Loader2 className="animate-spin" /> : <Save />} {editingId ? 'Save Changes' : 'Create Client'}
                  </button>
                </form>
              </div>
            )}

            {!loading && clients.length > 0 && !isFormOpen && (
              <>
                 {viewFilter !== 'All' && (
                   <div className={`mb-4 p-3 rounded-lg flex justify-between items-center animate-fade-in-down ${viewFilter === 'Commercial' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                      <span className="font-bold flex items-center gap-2">
                        {viewFilter === 'Commercial' ? <Building2 size={18}/> : <Home size={18}/>}
                        Viewing {viewFilter} Clients only
                      </span>
                      <button onClick={() => setViewFilter('All')} className="text-xs underline">Clear Filter</button>
                   </div>
                 )}
                 <div className="mb-4 relative">
                    <Search className="absolute left-4 top-3.5 text-slate-400 h-5 w-5" />
                    <input type="text" placeholder="Search clients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 p-3 rounded-full border border-slate-300 shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                 </div>
                 <div className="grid grid-cols-1 gap-4">
                  {filteredClientsList.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">No clients found in this category.</div>
                  ) : filteredClientsList.map((client) => (
                    <div key={client.id} className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 hover:shadow-md transition-all relative">
                      <div className="flex justify-between items-start mb-3 border-b border-slate-100 pb-3">
                        <div>
                          <div className="flex items-center gap-2">
                             <h3 className="text-xl font-bold text-slate-800">{client.firstName} {client.lastName}</h3>
                             {client.clientType === 'Commercial' 
                                ? <span className="bg-emerald-100 text-emerald-600 p-1 rounded"><Building2 size={14}/></span> 
                                : <span className="bg-blue-100 text-blue-600 p-1 rounded"><Home size={14}/></span>
                             }
                          </div>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs font-bold text-white bg-indigo-500 px-2 py-0.5 rounded-md">{client.frequency}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">${client.regularPrice}</div>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex gap-2 items-center"><MapPin size={16} className="text-indigo-400"/> <span className="font-medium text-slate-800">{client.address}</span></div>
                        <div className="flex gap-4 text-xs mt-2 text-slate-400">
                           {client.gateCode && <span className="flex gap-1 items-center"><Lock size={12}/> {client.gateCode}</span>}
                           <span className="flex gap-1 items-center"><Phone size={12}/> {client.phone}</span>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3 mt-4 pt-2 border-t border-slate-50">
                        <button onClick={() => handleEdit(client)} className="text-indigo-600 text-sm font-bold flex items-center gap-1"><Edit2 size={16} /> Edit</button>
                        <button onClick={() => handleDelete(client.id)} className="text-red-400 text-sm flex items-center gap-1 ml-2"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <DashboardView />
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-2 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50">
        <div className="max-w-md mx-auto flex justify-around">
          <button onClick={() => setActiveTab('clients')} className={`flex flex-col items-center p-2 rounded-xl w-full transition-all ${activeTab === 'clients' ? 'text-indigo-600' : 'text-slate-400 hover:bg-slate-50'}`}>
            <Users size={24} /><span className="text-xs font-bold mt-1">Clients</span>
          </button>
          <button onClick={() => setActiveTab('dashboard')} className={`flex flex-col items-center p-2 rounded-xl w-full transition-all ${activeTab === 'dashboard' ? 'text-indigo-600' : 'text-slate-400 hover:bg-slate-50'}`}>
            <LayoutDashboard size={24} /><span className="text-xs font-bold mt-1">Analytics</span>
          </button>
        </div>
      </div>
    </div>
  );
}
